/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { api, resolveImageUrl, BRAND } from "@/lib/divine";

const CATEGORIES = ["All", "Residential", "Commercial", "Vastu", "Modular Kitchen", "Home Renovation"];

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [reels, setReels] = useState([]);
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => { setActiveIdx(0); }, [active]);

  const gallery = active?.images?.length ? active.images : (active ? [active.image] : []);
  const next = () => setActiveIdx((i) => (i + 1) % gallery.length);
  const prev = () => setActiveIdx((i) => (i - 1 + gallery.length) % gallery.length);

  // Keyboard controls for the lightbox
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key === "Escape") setActive(null);
      else if (e.key === "ArrowRight" && gallery.length > 1) next();
      else if (e.key === "ArrowLeft" && gallery.length > 1) prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, gallery.length]);

  useEffect(() => {
  api.get("/projects")
    .then((r) => {
      const list = Array.isArray(r.data)
        ? r.data
        : (r.data?.projects || []);

      setProjects(list);
    })
    .catch(() => setProjects([]));
 
  api.get("/reels")
  .then((r) => {
    const list = Array.isArray(r.data)
      ? r.data
      : (r.data?.reels || []);

    setReels(list);
  })
  .catch(() => setReels([]));
  }, []);
  const filtered = filter === "All" ? projects : projects.filter((p) => p.category === filter);

  return (
    <div data-testid="projects-page" className="min-h-screen">
      <section className="pt-16 pb-12 border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="overline mb-6">Portfolio</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
            Our <span className="italic text-[#D4A437]">work</span>.
          </h1>
          <p className="text-[#6B6B6B] mt-6 max-w-xl">
            A curated look at homes, offices and spaces we've reimagined.
          </p>

          <div className="mt-12 flex flex-wrap gap-3">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                className={`text-xs tracking-[0.2em] uppercase px-5 py-3 border transition-all ${
                  filter === cat
                    ? "bg-[#D4A437] border-[#D4A437] text-[#0A0A0A]"
                    : "border-black/15 text-[#0A0A0A]/75 hover:border-[#D4A437] hover:text-[#D4A437]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* INSTAGRAM REELS */}
      {reels.length > 0 && (
      <section className="py-16 md:py-20 border-b border-black/5 bg-[#FAF7F0]" data-testid="reels-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <div className="overline mb-4">From the Studio</div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                Latest <span className="italic text-[#D4A437]">reels</span>.
              </h2>
              <p className="text-[#6B6B6B] mt-3 max-w-lg">
                A peek behind the scenes — site visits, finished spaces, and design wisdom.
              </p>
            </div>
            <a
              href={BRAND.instagramUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="follow-instagram"
              className="text-xs tracking-[0.2em] uppercase text-[#D4A437] hover:text-[#0A0A0A] inline-flex items-center gap-2 self-start md:self-end"
            >
              Follow @{BRAND.instagram} →
            </a>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {reels.map((r) => (
              <div
                key={r.id || r.code}
                data-testid={`reel-${r.code}`}
                className="relative border border-black/10 bg-white shadow-[0_20px_60px_-20px_rgba(212,164,55,0.15)] overflow-hidden"
                style={{ aspectRatio: "9 / 14" }}
              >
                <iframe
                  src={`https://www.instagram.com/reel/${r.code}/embed/`}
                  className="w-full h-full"
                  frameBorder="0"
                  scrolling="no"
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                  title={`Reel ${r.code}`}
                />
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {filtered.length === 0 ? (
            <p className="text-[#6B6B6B] text-center py-20">No projects in this category yet.</p>
          ) : (
            <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
              {filtered.map((p, idx) => (
                <button
                  key={p.id}
                  onClick={() => setActive(p)}
                  data-testid={`project-${idx}`}
                  className="zoomable relative w-full break-inside-avoid block group text-left mb-6"
                >
                  <img src={resolveImageUrl(p.image)} alt={p.title} className="w-full h-auto object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                  {p.images && p.images.length > 1 && (
                    <span className="absolute top-3 right-3 bg-[#D4A437] text-[#0A0A0A] text-[10px] tracking-[0.18em] uppercase font-semibold px-3 py-1">
                      {p.images.length} photos
                    </span>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-[#D4A437] mb-2">{p.category}</div>
                    <h3 className="font-serif text-xl md:text-2xl text-white">{p.title}</h3>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox with gallery */}
      {active && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 md:p-12"
          onClick={() => setActive(null)}
          data-testid="lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={`Project gallery — ${active.title}`}
        >
          <button
            className="absolute top-6 right-6 text-white text-4xl hover:text-[#D4A437]"
            data-testid="lightbox-close"
            onClick={() => setActive(null)}
            aria-label="Close"
          >
            ×
          </button>
          {gallery.length > 1 && (
            <>
              <button
                className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-white hover:text-[#D4A437] text-4xl md:text-5xl bg-black/40 hover:bg-black/60 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center"
                onClick={(e) => { e.stopPropagation(); prev(); }}
                data-testid="lightbox-prev"
                aria-label="Previous"
              >‹</button>
              <button
                className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-white hover:text-[#D4A437] text-4xl md:text-5xl bg-black/40 hover:bg-black/60 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center"
                onClick={(e) => { e.stopPropagation(); next(); }}
                data-testid="lightbox-next"
                aria-label="Next"
              >›</button>
            </>
          )}
          <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={resolveImageUrl(gallery[activeIdx])} alt={active.title} className="w-full max-h-[72vh] object-contain mx-auto" />
            <div className="mt-6 text-center text-white">
              <div className="overline mb-2">{active.category}</div>
              <h3 className="font-serif text-2xl md:text-3xl">{active.title}</h3>
              {active.description && <p className="text-white/70 mt-3 max-w-2xl mx-auto">{active.description}</p>}
              {gallery.length > 1 && (
                <div className="text-xs text-white/60 mt-4 tracking-widest">{activeIdx + 1} / {gallery.length}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    );
    }
